/* eslint-disable import/no-named-as-default */
import {isUndefined} from 'lodash';
import {fromJS, List, Map} from 'immutable';
import React from 'react';
import {Link} from 'react-router';
import {history} from 'client/history';
import sanitize from 'lib/sanitize';
import {callAction, callTargetedAction} from 'client/framework';
import appStore from 'client/AppStore';
import Modal, {ModalContent} from 'client/Modals/Modal.react';
import {hideModal, showModal} from 'client/Modals/ModalActions';
import TextInput from 'client/generic/Forms/TextInput/TextInput.react';
import {invalidatePartialInterest} from 'resources/mandark.resource';

import classroomStore from 'client/Classrooms/Classroom/ClassroomV2.store';
import WindowConfirm from 'client/generic/WindowConfirm/WindowConfirm.react';
import windowConfirmActions from 'generic/WindowConfirm/WindowConfirmActions';
import MasteryChart from 'generic/MasteryChart/MasteryChart.react';

import BackgroundTask from 'client/generic/BackgroundTask/BackgroundTask.react';
import {
  Card,
  TabNavigation,
  ButtonGroup,
  Button,
  IconButton,
  addToast,
  LoadingSpinnerLong
} from '@albert-io/atomic';
import ClipboardJS from 'clipboard';
import download from 'downloadjs';
import moment from 'moment';

import StatisticalSummary from '../../StatisticalSummary/StatisticalSummary.react';
import * as ListUtil from '../ListUtil/AssignmentListUtil';

import questionsActions from './Questions/QuestionsActions';
import questionsStore from './Questions/QuestionsStore';
import studentsActions from './Students/StudentsActions';
import studentsStore from './Students/StudentsStore';
import AssignmentDrilldownStore from './AssignmentDrilldownStore';
import AssignmentDrilldownActions from './AssignmentDrilldownActions';

import './assignment-drilldown.scss';

const deleteModalStoreName = 'assignment-teacher-drilldown-delete';

const tabItems = fromJS([
  {
    display: 'Students',
    to: 'student-detail'
  },
  {
    display: 'Questions',
    to: 'question-detail'
  }
]);
/* eslint-disable react/prop-types */
export default class AssignmentDrilldown extends React.Component {
  constructor(props) {
    super(props);

    this.clipboardInstance = null;
  }

  /* eslint-disable-next-line */
  UNSAFE_componentWillMount() {
    callAction(
      AssignmentDrilldownActions.INITIALIZE_ASSIGNMENT_DRILLDOWN,
      new Map({
        classroomId: this.props.params.classId,
        assignmentId: this.props.params.assignmentId
      })
    );

    /*
      Because we need stats from the students and questions stores,
      we need to initialize their stores with data before the components themselves are rendered
    */
    callAction(
      studentsActions.INITIALIZE_STUDENT_LIST,
      new Map({
        classroomId: this.props.params.classId,
        assignmentId: this.props.params.assignmentId
      })
    );

    callAction(
      questionsActions.INITIALIZE_QUESTION_LIST,
      new Map({
        assignmentId: this.props.params.assignmentId,
        classroomId: this.props.params.classId
      })
    );
  }

  componentDidUpdate() {
    // for copying assignment URL to clipboard
    if (this.copyUrlNode && !this.clipboardInstance) {
      this.clipboardInstance = new ClipboardJS(this.copyUrlNode);
    }
  }

  getShareActions() {
    const dateString = moment().format('YYYY-MM-DD');

    const generateExport = () => {
      const studentCSV = AssignmentDrilldownStore.generateStudentCSVData();
      const questionCSV = AssignmentDrilldownStore.generateQuestionCSVData();
      download(
        studentCSV,
        `${sanitize.filename(
          `${AssignmentDrilldownStore.getActiveAssignmentClassName()}-${AssignmentDrilldownStore.getAssignmentName()}-Assignment-Student-Summary-${dateString}`
        )}.csv`,
        'text/csv'
      );
      download(
        questionCSV,
        `${sanitize.filename(
          `${AssignmentDrilldownStore.getActiveAssignmentClassName()}-${AssignmentDrilldownStore.getAssignmentName()}-Assignment-Question-Summary-${dateString}`
        )}.csv`,
        'text/csv'
      );
    };

    return (
      <Button color='secondary' size='s' onClick={generateExport}>
        Basic (CSV)
      </Button>
    );
  }

  getProgressBarColorStyle(assignmentInfo) {
    if (assignmentInfo.get('progressValue') === assignmentInfo.get('progressEnd')) {
      return 'progress-meter--proficient';
    }
    return 'progress-meter--excelling';
  }

  getProgressBar(assignmentInfo) {
    if (isUndefined(assignmentInfo.get('progress'))) {
      return null;
    }
    return (
      <progress
        className={`progress-meter ${this.getProgressBarColorStyle(assignmentInfo)}`}
        value={assignmentInfo.get('progress')}
        max={100}
      />
    );
  }

  getAssignmentDescription() {
    if (
      !AssignmentDrilldownStore.hasActiveAssignment() ||
      !AssignmentDrilldownStore.hasAssignmentStats() ||
      /*
        once again we need student stats here to render a more in depth assignment summary.
        We are showing student mastery information in a graph in the first half of the page in addition to
        where it normally is shown in the student line items
      */
      !studentsStore.hasStudentStats() ||
      !questionsStore.hasQuestionStats()
    ) {
      return (
        <Card paddingLevel={2} className='assignment-drilldown__description max-width'>
          <div className='assignment-drilldown__description u-pad-t_5 u-pad-b_4'>
            <LoadingSpinnerLong />
          </div>
        </Card>
      );
    }

    const teacherId = classroomStore.getClassroomTeacher().getId();

    return (
      <Card paddingLevel={2} className='assignment-drilldown__description max-width'>
        <div className='assignment-drilldown__description-bar'>
          <Button
            color='secondary'
            size='s'
            onClick={() => {
              /*
                Reset our selected asignment data, and jump backwards to the assignment list

                Because the tabs augment the route, a simple .goBack is not sufficient, we need to be
                explicitly ridiculous here in order to prevent just jumping between tabs a bunch of times before
                .goBack takes us to the assignment list.
              */
              callAction(AssignmentDrilldownActions.RESET_STORE);
              history.pushState(null, `/classes/${this.props.params.classId}/assignments`);
            }}
          >
            Back
          </Button>
          <div className='assignment-drilldown__description-bar-heading'>
            <div className='assignment-drilldown__description-bar-heading-title'>
              {AssignmentDrilldownStore.getAssignmentName()}
            </div>
          </div>

          <ButtonGroup>
            {this.getShareActions()}
            <BackgroundTask
              task='excel-export'
              args={
                new Map({
                  filename: `${moment().format(
                    'YYYY-MM-DD'
                  )}-${AssignmentDrilldownStore.getActiveAssignmentClassName()}-${AssignmentDrilldownStore.getAssignmentName()}`,
                  path: '/reports/insight',
                  query: {
                    teacher: {
                      id: teacherId
                    },
                    classroom: {
                      id: this.props.params.classId
                    },
                    assignment: {
                      id: this.props.params.assignmentId
                    }
                  }
                })
              }
            >
              <Button color='secondary' size='s'>
                Detailed (Excel)
              </Button>
            </BackgroundTask>
            <IconButton
              onClick={() => {
                showModal('AssignmentOptions');
              }}
              icon='cog'
              label='Assignment options'
            />
          </ButtonGroup>
        </div>

        {this.getAssignmentDrilldownReport()}
        {this.getAssignmentOptionsModal()}
        {this.getDeleteConfirmationModal()}
      </Card>
    );
  }

  getStatsDisplay(assignmentInfo) {
    const classTotal =
      assignmentInfo.get('classes') > 1 ? (
        <div className='statistical-summary__stat'>
          Assigned To:{' '}
          <span className='statistical-summary__stat--result'>
            {' '}
            {ListUtil.getAssignedToText(assignmentInfo)}
          </span>
        </div>
      ) : null;
    return (
      <div className='statistical-summary__stats'>
        <div className='statistical-summary__stat'>
          Class:{' '}
          <span className='statistical-summary__stat--result'>
            {' '}
            {AssignmentDrilldownStore.getActiveAssignmentClassName()}
          </span>
        </div>
        {classTotal}
        <div className='statistical-summary__stat'>
          Due:{' '}
          <span className='statistical-summary__stat--result'>
            {assignmentInfo.get('dueDate').format('MM/DD/YYYY')}
          </span>
        </div>
        <div className='statistical-summary__stat'>
          Average Grade:{' '}
          <span className='statistical-summary__stat--result'>
            {assignmentInfo.get('averageGrade')}
          </span>
        </div>
        <div className='statistical-summary__stat'>
          Progress:{' '}
          <span className='statistical-summary__stat--result'>
            {' '}
            {ListUtil.getNumberCompletedHeadingForTeacher(assignmentInfo)}
          </span>
        </div>
      </div>
    );
  }

  getChartDisplay(statuses) {
    return (
      <div className='assignment-drilldown__statistical-summary-chart-wrapper'>
        <MasteryChart statuses={statuses} showKey size={300} lineWidth={36} showNAStatus />
      </div>
    );
  }

  getAssignmentDrilldownReport() {
    const {classId} = this.props.params;
    const assignmentInfo = new Map({
      title: AssignmentDrilldownStore.getAssignmentName(),
      assignmentId: AssignmentDrilldownStore.getAssignmentId(),
      completionDateTime: AssignmentDrilldownStore.getCompletionDateTime(classId),
      classes: AssignmentDrilldownStore.getActiveAssignmentStatsClasses(),
      progressEnd: AssignmentDrilldownStore.getActiveAssignmentStatsStudents(),
      progressValue: AssignmentDrilldownStore.getActiveAssignmentFinishedStudents().size,
      averageGrade: AssignmentDrilldownStore.getActiveAssignment().getAverageGradeForDisplay(),
      progress: AssignmentDrilldownStore.getActiveAssignmentStatsProgress(),
      dueDate: AssignmentDrilldownStore.getActiveAssignmentDueDate(classId)
    });

    /*
      XXX: here is where the students store that is supposed to govern only the student list is coupled to the
      assignment description. There is probably a much cleaner way to make this happen, but what is it.
    */
    const statuses = studentsStore.getStudentStatuses();
    const studentStatuses = AssignmentDrilldownStore.getActiveAssignmentStudents().reduce(
      (list, student) => {
        return list.push(statuses.get(student.get('id')));
      },
      new List()
    );

    return (
      <div>
        <StatisticalSummary>
          {this.getStatsDisplay(assignmentInfo)}
          {this.getChartDisplay(studentStatuses)}
        </StatisticalSummary>
      </div>
    );
  }

  getDeleteConfirmationModal() {
    // Normally we'd have access to an `assignmentInfo` object like you see in
    // `getAssignmentLevelStatistics` as in a list item component. The drilldown is a special case.
    // We can just give it what the util and the windowConfirm needs here.
    const classroomName = this.props.classroom?.getName();
    const assignmentToDelete = new Map({
      assignmentId: this.props.params.assignmentId,
      name: AssignmentDrilldownStore.getAssignmentName(),
      studentsInAssignment: AssignmentDrilldownStore.getActiveAssignmentStatsStudentsAllClasses()
    });
    const messageHeading = ListUtil.getDeleteAssignmentModalHeading(assignmentToDelete);
    const deleteMessage = ListUtil.getDeleteAssignmentModalCopy(assignmentToDelete, classroomName);
    return (
      <WindowConfirm
        hideToolbar
        messageHeading={messageHeading}
        message={deleteMessage}
        onConfirm={() => {
          callAction(AssignmentDrilldownActions.DELETE_ASSIGNMENT, this.props.params.assignmentId);
          AssignmentDrilldownStore.getDeleteAssignmentPromise()
            .then(() => {
              invalidatePartialInterest({resourcePath: ['assignments_v3']});
              history.pushState(null, `/classes/${this.props.params.classId}/assignments`);
            })
            .catch(() => {
              addToast({
                type: 'danger',
                message: 'Unable to delete assignment'
              });
            });
        }}
        storeName={deleteModalStoreName}
        confirmButtonText='Confirm'
        confirmButtonColor='aqua'
      />
    );
  }

  makeShareUrl(assignmentId) {
    if (!process.env.IS_BROWSER) {
      return '';
    }
    const base = `${global.window.location.protocol}//${global.window.location.host}`;
    return `${base}/assignment/${assignmentId}`;
  }

  getAssignmentOptionsModal() {
    const {assignmentId, classId} = this.props.params;

    return (
      <Modal modalWrapperClass='modal--assignment-drilldown__options' modalKey='AssignmentOptions'>
        <ModalContent>
          <div className='assignment-drilldown__options-menu'>
            <div className='assignment-drilldown__button-wrapper'>
              {/* eslint-disable react/button-has-type */}
              <button
                className='assignment-drilldown__button assignment-drilldown__button--edit'
                data-testid='assignment-drilldown__edit-btn'
                onClick={() => {
                  hideModal('AssignmentOptions');
                  history.pushState(
                    null,
                    `/create-assignment/assignment/${assignmentId}/classroom/${classId}/edit`
                  );
                }}
              >
                Edit settings
              </button>
            </div>
            <div className='assignment-drilldown__button-wrapper'>
              <button
                className='assignment-drilldown__button assignment-drilldown__button--delete'
                onClick={() => {
                  hideModal('AssignmentOptions');
                  callTargetedAction({
                    name: windowConfirmActions.SHOW_MODAL,
                    targetStore: deleteModalStoreName
                  });
                }}
              >
                Delete
              </button>
            </div>
            <hr />
            <TextInput value={this.makeShareUrl(assignmentId)} />
            <div className='assignment-drilldown__button-wrapper'>
              <button
                data-clipboard-text={this.makeShareUrl(assignmentId)}
                className='assignment-drilldown__button assignment-drilldown__button--copy'
                ref={(node) => {
                  this.copyUrlNode = node;
                }}
              >
                Copy shareable link
              </button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    );
  }

  /*

    TODO: The tables being used here are non-paginated tables that
    will let a teacher scroll through them with a faux table header rendered
    so they don't get confused looking at columns as they scroll.

    This is the same table in student level insights, and it could stand to be componentized
    somehow. Possibly providing a function to spit out React elements for each 'datum' in
    every column. That'd be kinda cool.

    Or we could use a normal table with pagination mechanisms. idk.

    For now, the path of least resistance is the one we're kicking dirt on.
  */
  render() {
    const tabs = tabItems.map((item) => {
      const toLink = `/classes/${this.props.params.classId}/assignments/${
        this.props.params.assignmentId
      }/${item.get('to')}`;
      return (
        <TabNavigation.Tab
          as={Link}
          to={toLink}
          isActive={toLink === appStore.routerProps.location.pathname}
        >
          {item.get('display')}
        </TabNavigation.Tab>
      );
    });

    return (
      <>
        {this.getAssignmentDescription()}
        <Card className='assignment-drilldown__detail max-width'>
          <TabNavigation align='left'>{tabs}</TabNavigation>
          {this.props.children && React.cloneElement(this.props.children, {})}
        </Card>
      </>
    );
  }
}
